import { useEffect, useRef } from 'react'

export default function ProspectsPreviewGrid({ prospects, totalResults }) {
  const gridRef = useRef(null)

  useEffect(() => {
    if (gridRef.current) {
      const cards = gridRef.current.querySelectorAll('.preview-prospect-card')
      cards.forEach((card, index) => {
        card.style.opacity = '0'
        card.style.transform = 'translateY(20px)'
        setTimeout(() => {
          card.style.transition = 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
          card.style.opacity = '1'
          card.style.transform = 'translateY(0)'
        }, index * 80)
      })
    }
  }, [prospects])

  const getInitials = (prospect) => {
    return `${prospect.firstName?.charAt(0) || ''}${prospect.lastName?.charAt(0) || ''}`.toUpperCase()
  }

  return (
    <div className="preview-results-section">
      <div className="preview-results-header">
        <h3>Aperçu des {prospects.length} premiers prospects</h3>
        <p className="preview-results-subtitle">
          Sur un total de <strong>{totalResults.toLocaleString()}</strong> prospects disponibles
        </p>
      </div>

      <div className="preview-prospects-grid" ref={gridRef}>
        {prospects.map((prospect, index) => {
          const initials = getInitials(prospect)

          return (
            <div key={index} className="preview-prospect-card">
              <div className="prospect-card-header">
                <div className={`prospect-avatar-small ${prospect.logoUrl ? 'has-logo' : ''}`}>
                  {prospect.logoUrl ? (
                    <img
                      src={prospect.logoUrl}
                      alt={prospect.companyName}
                      className="avatar-logo"
                      onError={(e) => (e.target.style.display = 'none')}
                    />
                  ) : (
                    <span className="avatar-initials-small">{initials}</span>
                  )}
                </div>
                <div className="prospect-status-badges">
                  {prospect.primaryEmail && (
                    <span className="status-icon contact-icon" title="Email disponible">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M3 14C3 11.2386 5.23858 9 8 9C10.7614 9 13 11.2386 13 14" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    </span>
                  )}
                  {prospect.companyWebsite && (
                    <span className="status-icon website-icon" title="Site web disponible">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M2 8h12M8 2c1.5 1.5 2 4 2 6s-.5 4.5-2 6M8 2c-1.5 1.5-2 4-2 6s.5 4.5 2 6" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>

              <div className="prospect-details">

                {/* Nom & poste */}
                <div className="prospect-name-full">
                  {prospect.firstName} {prospect.lastName}
                </div>
                <div className="prospect-job-title">{prospect.jobTitle || '—'}</div>

                {/* Entreprise */}
                {prospect.companyName && (
                  <div className="prospect-company-name">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <rect x="2" y="2" width="8" height="8" stroke="currentColor" strokeWidth="1" />
                      <line x1="2" y1="5" x2="10" y2="5" stroke="currentColor" strokeWidth="1" />
                      <line x1="6" y1="2" x2="6" y2="10" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    {prospect.companyName}
                  </div>
                )}

                {/* Secteur d'activité */}
                {prospect.companyIndustry && (
                  <div className="prospect-industry">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 10L6 2L10 10" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {prospect.companyIndustry}
                  </div>
                )}

                {/* OPCO */}
                {prospect.opco && (
                  <div className="prospect-opco">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <rect x="1" y="3" width="10" height="6" rx="1" stroke="currentColor" strokeWidth="1" />
                      <path d="M1 4L6 7L11 4" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    OPCO : {prospect.opco}
                  </div>
                )}

                {/* Zone géographique */}
                {prospect.zone && (
                  <div className="prospect-zone">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M6 1C4 1 2 2.5 2 4.5C2 7 6 11 6 11C6 11 10 7 10 4.5C10 2.5 8 1 6 1Z" stroke="currentColor" strokeWidth="1" />
                      <circle cx="6" cy="4.5" r="1" fill="currentColor" />
                    </svg>
                    {prospect.zone}
                  </div>
                )}

                {/* Adresse complète */}
                {prospect.adresse_complete && (
                  <div className="prospect-adresse">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <rect x="1" y="3" width="10" height="7" rx="1" stroke="currentColor" strokeWidth="1" />
                      <path d="M4 3V2a2 2 0 014 0v1" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    {prospect.adresse_complete}
                  </div>
                )}

                {/* Email
                {prospect.primaryEmail && (
                  <div className="prospect-email">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <rect x="1" y="3" width="10" height="6" rx="1" stroke="currentColor" strokeWidth="1" />
                      <path d="M1 4L6 7L11 4" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    {prospect.primaryEmail}
                  </div>
                )} */}

                {prospect.primaryEmail && (
                  <div className="prospect-email" style={{ wordBreak: 'break-all' }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <rect x="1" y="3" width="10" height="6" rx="1" stroke="currentColor" strokeWidth="1" />
                      <path d="M1 4L6 7L11 4" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    {prospect.primaryEmail}
                  </div>
                )}

                {prospect.companyWebsite && (
                  <div className="prospect-website" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" />
                      <path d="M1 6h10M6 1c1.5 1.5 2 3 2 5s-.5 3.5-2 5M6 1C4.5 2.5 4 4 4 6s.5 3.5 2 5" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    {prospect.companyWebsite}
                  </div>
                )}

                {/* Téléphone */}
                {prospect.primaryPhone ? (
                  <div className="prospect-phone">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 2h3l1 3-1.5 1.5a7 7 0 003 3L9 8l3 1v3a1 1 0 01-1 1C5 13 -1 7-1 1a1 1 0 011-1z" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    {prospect.primaryPhone}
                  </div>
                ) : (
                  <div className="prospect-phone prospect-phone--missing" style={{ opacity: 0.4 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 2h3l1 3-1.5 1.5a7 7 0 003 3L9 8l3 1v3a1 1 0 01-1 1C5 13 -1 7-1 1a1 1 0 011-1z" stroke="currentColor" strokeWidth="1" />
                    </svg>
                    Tél. non disponible
                  </div>
                )}

              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}